import React, {useContext, useEffect, useRef} from "react";
import CustomPopup from "../../../components/CustomPopup";
import '../../../global/css/container.css'
import '../../../global/css/global-display.css'
import '../../../global/css/font-style.css'
import {ThemeContext} from "../../../providers/themeContext/theme-context.tsx";
import {PageEnum} from "../../../types/IPage.ts";
import html2canvas from "html2canvas";
import {isEmptyOrNullOrUndefined} from "../../../common/utils/Datautil.ts";
import {GAEvents} from "../../../common/utils/GA4/GAEvents.ts";
import {ToastContext} from "../../../components/CustomToast/ToastProvider.tsx";
import {decimalToPercentage} from "../../../common/utils/num-format.ts";
import {formatTime} from "../../../common/utils/date-format.ts";
import {ShareUtil} from "../../../common/utils/share-util/shareUtil.ts";
import {IDailyGameCorrectWord, IDailyWordStatus} from "../../../common/utils/http/IResponse.ts";
import {UrlConfig} from "../../../common/config/url-config.ts";

type DailyGameEndDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    switchPage: (page: PageEnum) => void;
    dailyGameStatus?: IDailyWordStatus | null,
    correctWord: IDailyGameCorrectWord | null,
    handleShareReward: () => void;
};

// daily 游戏结束时的弹窗
const DailyGameEndDialog: React.FC<DailyGameEndDialogProps> = ({
                                                                   isOpen,
                                                                   onClose,
                                                                   switchPage,
                                                                   dailyGameStatus,
                                                                   correctWord,
                                                                   handleShareReward
                                                               }) => {
    // 主题全局管理器
    const {imgUrls, theme} = useContext(ThemeContext);
    const {addToast} = useContext(ToastContext);

    useEffect(() => {
        if (isOpen) {
            // GA埋点
            GAEvents.dailyGameEndDialogShow(dailyGameStatus?.gameStatus === 2 ? 'Fail' : 'Success');
        }
    }, [isOpen]);

    const targetRef = useRef(null)

    // 跳转到挑战页面
    const jumpToChallengePage = () => {
        // GA埋点
        GAEvents.dailyGameEndDialogAIClick(dailyGameStatus?.gameStatus === 2 ? 'Fail' : 'Success');
        switchPage(PageEnum.CHALLENGE);
    }

    // 对组件截图
    const captureScreenshot = () => {
        // GA埋点
        GAEvents.dailyGameEndDialogShareClick(dailyGameStatus?.gameStatus === 2 ? 'Fail' : 'Success');
        const node = targetRef.current;
        if (node) {
            // 设置node的高度
            html2canvas(node, {scale: 2, useCORS: true})
                .then((canvas) => {
                    // 这里创建一个更高一点的背景，然后将canvas放进去
                    const newCanvas = document.createElement('canvas');
                    newCanvas.width = canvas.width;
                    newCanvas.height = canvas.height + 40; // 增加的高度
                    const ctx = newCanvas.getContext('2d');
                    if (ctx) {
                        ctx.fillStyle = '#ffffff';
                        ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);
                        ctx.drawImage(canvas, 0, 0);
                    }
                    // 获取图片dataUrl
                    const dataUrl = newCanvas.toDataURL('image/png');

                    ShareUtil.share(new URL(UrlConfig.SHARE_URL), (e) => {
                        addToast({type: "warning", message: `Share error：${e}`})
                    }, dataUrl)
                })
                .catch((error) => {
                    console.error('Share error', error);
                }).finally(() => {
                handleShareReward();
            });
        }
    };

    const handleClickNewLink = (newUrl: string, newsId: string, newsTitle: string) => {
        if (isEmptyOrNullOrUndefined(newsId) || newsId !== "") {
            // 只获取这个字符串_前面的
            const newId = newsId.split('_')[0];
            // GA埋点
            GAEvents.dailyGameEndDialogNewsClick(dailyGameStatus?.gameStatus === 2 ? 'Fail' : 'Success', newId, newsTitle);
        }
        location.href = newUrl;
    }

    return (
        <CustomPopup isOpen={isOpen} onClose={onClose}>
            <div style={theme?.dailyGameEndDialogBg ? {
                backgroundImage: `url(${theme?.dailyGameEndDialogBg})`,
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
            } : {}}
                 className={`width-300 height-550 ${theme?.dailyGameEndDialogBg ? '' : 'bg-color-white'} border-radius-10 display-fixed-center`}>
                <div id="share-daily-end-div" ref={targetRef}>
                    {!theme?.dailyGameEndDialogBg &&
                        <img className="common-dialog-embellish-bg-left-top" src={imgUrls.leftTopBgDialogUrl}
                             alt="pic"/>}
                    {!theme?.dailyGameEndDialogBg &&
                        <img className="common-dialog-embellish-bg-right-bottom" src={imgUrls.rightBottomBgDialogUrl}
                             alt="pic"/>}
                    {/*关闭按钮*/}
                    <img className="exit-close-btn" onClick={onClose}
                         src={theme?.dailyGameEndDialogCloseIcon ?? imgUrls.closerBtnGreyUrl} alt="pic"/>
                    {/*标题*/}
                    <div style={{color: theme?.gameDescriptionTextColor}}
                         className="my-roboto-bold padding-top-14 font-size-16 text-center">{dailyGameStatus?.gameStatus === 2 ? "Thanks for playing!" : "Congratulations!"}</div>
                    {/*数据列表bar*/}
                    <div style={theme?.dailyGameEndDialogStatisticsCardBg ? {
                        backgroundImage: `url(${theme?.dailyGameEndDialogStatisticsCardBg})`,
                        backgroundSize: "cover"
                    } : {}}
                         className={`margin-top-16 border-radius-6 ${theme?.dailyGameEndDialogStatisticsCardBg ? '' : 'bg-color-grey'} height-68 width-273 grid-4-columns margin-left-right-auto`}>
                        <div style={{color: theme?.gameDescriptionTextColor}}
                             className="display-flex-column display-flex-align-items-center display-flex-justify-content-center">
                            <div className="my-roboto-bold font-size-18">{dailyGameStatus?.played ?? 1}</div>
                            <div style={{color: theme?.gameDescriptionTextColor}}
                                 className="font-size-10 my-roboto-light color-grey margin-top-2">Played
                            </div>
                        </div>
                        <div style={{color: theme?.gameDescriptionTextColor}}
                             className="display-flex-column display-flex-align-items-center display-flex-justify-content-center">
                            <div style={{color: theme?.gameDescriptionTextColor}}
                                 className="my-roboto-bold font-size-18">{decimalToPercentage(dailyGameStatus?.winRate ?? 1)}</div>
                            <div style={{color: theme?.gameDescriptionTextColor}}
                                 className="font-size-10 my-roboto-light color-grey margin-top-2">Win%
                            </div>
                        </div>
                        <div
                            className="display-flex-column display-flex-align-items-center display-flex-justify-content-center">

                            {/*显示本地游戏时间，如果失败就是1800，如果成功，就是显示已经被扣除的时间*/}
                            <div style={{color: theme?.gameDescriptionTextColor}}
                                 className="my-roboto-bold font-size-18">{formatTime(dailyGameStatus?.playTime ?? 1800)}</div>

                            <div style={{color: theme?.gameDescriptionTextColor}}
                                 className="font-size-10 my-roboto-light color-grey margin-top-2">Time
                            </div>
                        </div>
                        <div
                            className="display-flex-column display-flex-align-items-center display-flex-justify-content-center">
                            {/*显示玩的时候总共猜的的词次数*/}
                            <div style={{color: theme?.gameDescriptionTextColor}}
                                 className="my-roboto-bold font-size-18">{dailyGameStatus?.totalWords?.length ?? 0}</div>
                            <div style={{color: theme?.gameDescriptionTextColor}}
                                 className="font-size-10 my-roboto-light color-grey margin-top-2">Attempts
                            </div>
                        </div>
                    </div>
                    {/*正确单词框*/}
                    <div
                        style={{backgroundImage: `url(${theme?.dailyGameEndDialogAnswerCardBg ?? imgUrls.gameDialogCardBgUrl})`}}
                        className="height-142 width-273 margin-left-right-auto border-radius-10 game-fail-word-box margin-top-7 display-flex-column display-flex-justify-content-center display-flex-align-items-center">
                        <div style={{color: theme?.gameDescriptionTextColor}}
                             className="margin-top-20 font-size-16 color-grey my-roboto-regular">Answer
                        </div>
                        <div className="grid-5-columns grid-gap-3">
                            {dailyGameStatus?.correctWord?.toUpperCase().split("").map((letter, index) => {
                                return (
                                    <div key={letter + index}
                                         style={theme?.GameEndDialogLetterBg ? {
                                             backgroundImage: `url(${theme?.GameEndDialogLetterBg})`,
                                             backgroundSize: 'cover'
                                         } : {}}
                                         className="display-flex-column display-flex-align-items-center display-flex-justify-content-center border-radius-4 width-33 height-40 bg-color-liner-white-green">
                                        <div className="font-size-27 color-white my-roboto-bold ">{letter}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <div
                            className="margin-bottom-20 display-flex-row display-flex-align-items-center display-flex-justify-content-center">
                            <div style={{color: theme?.gameDescriptionTextColor}}
                                 className="font-size-14 my-roboto-regular">{dailyGameStatus?.gameStatus === 3 ? 'Daily complete! Try a new challenge!' : 'So close! Try again tomorrow!'}</div>
                        </div>
                    </div>
                    {/*word expansion*/}
                    <div style={theme?.dailyGameEndDialogWordCardBg ? {
                        backgroundImage: `url(${theme?.dailyGameEndDialogWordCardBg})`,
                        backgroundSize: "cover"
                    } : {}}
                         className={`${theme?.dailyGameEndDialogWordCardBg ? '' : 'bg-color-grey'} height-205 width-245 margin-top-6 padding-top-14 border-radius-6 margin-left-right-auto padding-left-14 padding-right-14`}>
                        <div style={{color: theme?.gameDescriptionTextColor, opacity: 0.7}}
                             className="font-size-16 padding-bottom-6 display-flex-row display-flex-justify-content-center display-flex-align-items-center color-grey my-roboto-regular">{isEmptyOrNullOrUndefined(correctWord?.popularSay) ? 'Word Expansion' : 'Reference'}</div>
                        {/*分割线*/}
                        <div className="split-line width-246 margin-bottom-6"/>
                        {
                            isEmptyOrNullOrUndefined(correctWord?.popularSay) ?
                                // 新闻文章
                                correctWord?.wordPicture ?
                                    <div
                                        onClick={() => handleClickNewLink(correctWord?.newLink ?? "", correctWord?.wordId ?? '', correctWord?.wordTitle ?? '')}
                                        className="display-flex-column display-flex-justify-content-center display-flex-align-items-center">
                                        <img
                                            className="width-190 height-108  border-radius-6 img-no-repeat-center-contain"
                                            src={correctWord?.wordPicture}
                                            alt="pic"/>
                                        <div style={{color: theme?.gameDescriptionTextColor}}
                                             className="ellipsis-2-line width-200 margin-top-6 text-center my-roboto-medium font-size-14">{correctWord?.wordTitle}</div>
                                    </div> :
                                    // 如果啥都没有就显示默认的名言警句
                                    <div style={{color: theme?.gameDescriptionTextColor}}
                                         className="my-roboto-bold font-size-14 text-center">Great works not by
                                        strength,
                                        but by insist to complete.</div> :
                                // 名言警句
                                <div style={{color: theme?.gameDescriptionTextColor}}
                                     className="my-roboto-bold font-size-14 text-center">{correctWord?.popularSay}</div>
                        }
                    </div>
                </div>
                {/*按钮部分*/}
                <div
                    className="height-43 margin-top-6 display-flex-row width-273 display-flex-justify-content-center display-flex-align-items-center display-flex-align-content margin-left-right-auto">
                    {/*分享按钮*/}
                    <div onClick={captureScreenshot}
                         className={`${theme?.dailyGameEndDialogShareBtn ? '' : 'bg-color-liner-green-blue'} height-43 width-43 border-radius-5  display-flex-column display-flex-align-items-center display-flex-justify-content-center`}>
                        {theme?.dailyGameEndDialogShareBtn ?
                            <img className="height-43 width-43 bg-img-cover-no-repeat-center"
                                 src={theme?.dailyGameEndDialogShareBtn} alt="pic"/>
                            :
                            <img className="width-18 height-18" src={imgUrls.shareIconBlueUrl} alt="pic"/>}
                    </div>
                    {/*挑战按钮*/}
                    <div onClick={jumpToChallengePage}
                         style={theme?.dailyGameEndDialogChallengeBtnBg ? {
                             backgroundImage: `url(${theme?.dailyGameEndDialogChallengeBtnBg})`,
                             backgroundSize: "cover"
                         } : {}}
                         className={`${theme?.dailyGameEndDialogChallengeBtnBg ? '' : 'bg-color-liner-green-challenge'} margin-left-8 width-222 height-43 border-radius-5 display-flex-column display-flex-justify-content-center display-flex-align-items-center`}>
                        <div className="font-size-16 my-roboto-medium color-white">Challenge</div>
                    </div>
                </div>
            </div>
        </CustomPopup>
    );
}
export default DailyGameEndDialog;